import { Button, Select } from '@mantine/core';
import {
  IconChevronDown,
  IconInfoCircle,
  IconKey,
  IconLanguage,
  IconLogout,
  IconPackage,
  IconRefresh,
  IconShield,
  IconShieldCheck,
  IconSparkles,
  IconUserCircle,
} from '@tabler/icons-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { CardFooter, Fact, Facts, settingsClasses } from './SettingsLayout.js';

const ICON = { size: 18, stroke: 1.8 } as const;

export interface LanguageCardProps {
  language: string;
  languages: readonly { value: string; label: string }[];
  onLanguage: (language: string) => void;
  /** Yalnız gemide: görünüm boyutu (M6f G6-12). */
  displaySize?: { value: number; options: readonly number[]; onChange: (value: number) => void };
  /** Örnek tarih ve saat (testte sabit saat için). */
  now?: Date;
}

/**
 * "Language & Regional" (M6f G6-35, G6-36): etiketli dil seçimi (≈ 280 px), sabit tarih biçimi,
 * gemide görünüm boyutu.
 */
export function LanguageCard({
  language,
  languages,
  onLanguage,
  displaySize,
  now,
}: LanguageCardProps) {
  const { t, i18n } = useTranslation();
  // "100%" İngilizcede, "%100" Türkçede
  const percent = new Intl.NumberFormat(i18n.resolvedLanguage ?? 'en', { style: 'percent' });
  return (
    <Panel title={t('settingsScreen.language.title')} icon={<IconLanguage {...ICON} />} divider>
      <Facts>
        <Fact
          label={t('settingsScreen.language.interface')}
          help={t('settingsScreen.language.help')}
        >
          <Select
            className={settingsClasses.select}
            aria-label={t('settingsScreen.language.interface')}
            data={languages.map((item) => ({ value: item.value, label: item.label }))}
            value={language}
            allowDeselect={false}
            rightSection={<IconChevronDown size={16} />}
            rightSectionPointerEvents="none"
            onChange={(value) => {
              if (value) onLanguage(value);
            }}
          />
        </Fact>
        {displaySize ? (
          <Fact
            label={t('settingsScreen.language.displaySize')}
            help={t('settingsScreen.language.displaySizeHelp')}
          >
            <Select
              className={settingsClasses.select}
              aria-label={t('settingsScreen.language.displaySize')}
              data={displaySize.options.map((option) => ({
                value: String(option),
                label: percent.format(option),
              }))}
              value={String(displaySize.value)}
              allowDeselect={false}
              rightSection={<IconChevronDown size={16} />}
              rightSectionPointerEvents="none"
              onChange={(value) => {
                if (value) displaySize.onChange(Number(value));
              }}
            />
          </Fact>
        ) : null}
        <Fact label={t('settingsScreen.language.dateFormat')}>
          {t('settingsScreen.language.dateFormatValue', {
            example: formatDateTime(now ?? new Date()),
          })}
        </Fact>
      </Facts>
    </Panel>
  );
}

/** Ofiste iki adımlı doğrulama (sahip 2026-11-01): isteğe bağlı, kişi kendisi açar ve kapatır. */
export interface AccountTwoStep {
  enabled: boolean;
  /**
   * `false`: tanıtım şirketi ya da paylaşılan test hesabı. Girişte kod sorulmaz (ADR 0148 §6):
   * `enabled` ne olursa olsun rozet, açıklama ve düğme yerine kısa not.
   */
  available: boolean;
  onEnable?: (() => void) | undefined;
  onDisable?: (() => void) | undefined;
}

export interface AccountCardProps {
  name: string | null;
  email: string | null;
  /** Gemide rütbe, ofiste rol. */
  title?: { kind: 'rank' | 'role'; value: string | null } | undefined;
  /** Giriş güvenliğinin tek satırlık özeti; `twoStep` verilince güvenlik bölümü onun yerini alır. */
  security?: string | undefined;
  /**
   * Verilirse kartta "Sign-in security" bölümü: parola satırı ve iki adımlı doğrulama satırı
   * (rozet ve aç/kapat). Verilmezse kart eskisi gibidir (gemi uygulaması).
   */
  twoStep?: AccountTwoStep | undefined;
  onChangePassword?: (() => void) | undefined;
  onSignOut?: (() => void) | undefined;
}

/** "Account": ad, e-posta, rütbe ya da rol; parola değiştirme ve çıkış (M6f G6-32). */
export function AccountCard({
  name,
  email,
  title,
  security,
  twoStep,
  onChangePassword,
  onSignOut,
}: AccountCardProps) {
  const { t } = useTranslation();
  // Güvenlik bölümü varken parola düğmesi kendi satırındadır; altta yalnız çıkış kalır
  const footerPassword = twoStep ? undefined : onChangePassword;
  return (
    <Panel title={t('settingsScreen.account.title')} icon={<IconUserCircle {...ICON} />} divider>
      <Facts>
        <Fact label={t('settingsScreen.account.name')}>{name ?? '—'}</Fact>
        <Fact label={t('settingsScreen.account.email')}>{email ?? '—'}</Fact>
        {title ? (
          <Fact label={t(`settingsScreen.account.${title.kind}`)}>{title.value ?? '—'}</Fact>
        ) : null}
        {security && !twoStep ? (
          <Fact label={t('settingsScreen.account.security')}>{security}</Fact>
        ) : null}
      </Facts>
      {twoStep ? <SignInSecurity twoStep={twoStep} onChangePassword={onChangePassword} /> : null}
      {footerPassword || onSignOut ? (
        <CardFooter>
          {footerPassword ? (
            <Button variant="default" leftSection={<IconKey size={16} />} onClick={footerPassword}>
              {t('settingsScreen.account.changePassword')}
            </Button>
          ) : null}
          {onSignOut ? (
            <Button variant="default" leftSection={<IconLogout size={16} />} onClick={onSignOut}>
              {t('settingsScreen.account.signOut')}
            </Button>
          ) : null}
        </CardFooter>
      ) : null}
    </Panel>
  );
}

/** Account kartının "Sign-in security" bölümü: parola ve iki adımlı doğrulama satırları. */
function SignInSecurity({
  twoStep,
  onChangePassword,
}: {
  twoStep: AccountTwoStep;
  onChangePassword?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const twoStepId = useId();
  // Kullanılamayan hesapta kurulu TOTP girişte sorulmaz (ADR 0148 §6): "On" gösterilmez
  const on = twoStep.available && twoStep.enabled;
  const action = !twoStep.available ? null : on ? (
    twoStep.onDisable ? (
      <Button variant="default" onClick={twoStep.onDisable} aria-describedby={twoStepId}>
        {t('settingsScreen.account.turnOff')}
      </Button>
    ) : null
  ) : twoStep.onEnable ? (
    <Button
      variant="light"
      leftSection={<IconShieldCheck size={16} />}
      onClick={twoStep.onEnable}
      aria-describedby={twoStepId}
    >
      {t('settingsScreen.account.turnOn')}
    </Button>
  ) : null;
  return (
    <section className={settingsClasses.security} aria-labelledby={headingId}>
      <h3 id={headingId} className={settingsClasses.securityHeading}>
        {t('settingsScreen.account.security')}
      </h3>
      <ul className={settingsClasses.securityList}>
        <li className={settingsClasses.securityRow}>
          <span className={settingsClasses.securityIcon} aria-hidden>
            <IconKey size={18} stroke={1.8} />
          </span>
          <div className={settingsClasses.securityBody}>
            <span className={settingsClasses.securityName}>
              {t('settingsScreen.account.password')}
            </span>
            <span className={settingsClasses.securityHelp}>
              {t('settingsScreen.account.passwordHelp')}
            </span>
          </div>
          {onChangePassword ? (
            <div className={settingsClasses.securityAction}>
              <Button variant="default" onClick={onChangePassword}>
                {t('settingsScreen.account.changePassword')}
              </Button>
            </div>
          ) : null}
        </li>
        <li className={settingsClasses.securityRow} data-on={on || undefined}>
          <span className={settingsClasses.securityIcon} aria-hidden>
            {on ? (
              <IconShieldCheck size={18} stroke={1.8} />
            ) : (
              <IconShield size={18} stroke={1.8} />
            )}
          </span>
          <div className={settingsClasses.securityBody}>
            <span className={settingsClasses.securityName} id={twoStepId}>
              {t('settingsScreen.account.twoStep')}
              {!twoStep.available ? null : on ? (
                <StatusBadge tone="success" withIcon>
                  {t('settingsScreen.account.twoStepOn')}
                </StatusBadge>
              ) : (
                <StatusBadge tone="neutral">{t('settingsScreen.account.twoStepOff')}</StatusBadge>
              )}
            </span>
            <span className={settingsClasses.securityHelp}>
              {twoStep.available
                ? t('settingsScreen.account.twoStepHelp')
                : t('settingsScreen.account.twoStepUnavailable')}
            </span>
          </div>
          {action ? <div className={settingsClasses.securityAction}>{action}</div> : null}
        </li>
      </ul>
    </section>
  );
}

export type SoftwareStatus =
  | { state: 'office' }
  | { state: 'upToDate' }
  | { state: 'downloading' }
  | { state: 'ready'; version: string; onInstall: () => void }
  | { state: 'disabled' }
  | { state: 'error' };

/** "Software": ürün adı her zaman Felixmate ve sürüm (ADR 0019; NR496 4.1.2), güncelleme. */
export function SoftwareCard({
  version,
  status,
  onAbout,
  onWhatsNew,
  whatsNewUnseen = false,
}: {
  version: string | null;
  status: SoftwareStatus;
  /** "About Felixmate" penceresini açar (M9a). */
  onAbout?: () => void;
  /** Yenilikler penceresini açar (M17, ADR 0064). */
  onWhatsNew?: () => void;
  /** Görülmemiş sürüm notu var: düğmede küçük "New" rozeti. */
  whatsNewUnseen?: boolean;
}) {
  const { t } = useTranslation();
  const badge =
    status.state === 'ready' ? (
      <StatusBadge tone="info" withIcon>
        {t('settingsScreen.software.ready', { version: status.version })}
      </StatusBadge>
    ) : status.state === 'downloading' ? (
      <StatusBadge tone="info">{t('settingsScreen.software.downloading')}</StatusBadge>
    ) : status.state === 'error' ? (
      <StatusBadge tone="warning" withIcon>
        {t('settingsScreen.software.error')}
      </StatusBadge>
    ) : status.state === 'upToDate' ? (
      <StatusBadge tone="success" withIcon>
        {t('settingsScreen.software.upToDate')}
      </StatusBadge>
    ) : (
      <span>
        {status.state === 'office'
          ? t('settingsScreen.software.office')
          : t('settingsScreen.software.disabled')}
      </span>
    );
  return (
    <Panel title={t('settingsScreen.software.title')} icon={<IconPackage {...ICON} />} divider>
      <Facts compact>
        <Fact label={t('settingsScreen.software.product')}>Felixmate</Fact>
        <Fact label={t('settingsScreen.software.version')}>{version ?? '—'}</Fact>
        <Fact label={t('settingsScreen.software.updates')}>{badge}</Fact>
      </Facts>
      {status.state === 'ready' || onAbout || onWhatsNew ? (
        <CardFooter>
          {onWhatsNew ? (
            <Button
              variant="default"
              leftSection={<IconSparkles size={16} />}
              rightSection={
                whatsNewUnseen ? (
                  <StatusBadge tone="info">{t('settingsScreen.whatsNew.new')}</StatusBadge>
                ) : null
              }
              onClick={onWhatsNew}
            >
              {t('settingsScreen.whatsNew.open')}
            </Button>
          ) : null}
          {onAbout ? (
            <Button variant="default" leftSection={<IconInfoCircle size={16} />} onClick={onAbout}>
              {t('settingsScreen.about.open')}
            </Button>
          ) : null}
          {status.state === 'ready' ? (
            <Button leftSection={<IconRefresh size={16} />} onClick={status.onInstall}>
              {t('settingsScreen.software.install')}
            </Button>
          ) : null}
        </CardFooter>
      ) : null}
    </Panel>
  );
}
